/**
 * Shared types for sheet-driven bulk imports.
 *
 * Each importable sheet in the organizer workbook (Users, Locations,
 * Sessions, Groups, Flights, Lodging, Transport, Other Travel) is described
 * by an `ImportSpec`. The spec is the single source of truth for:
 *
 *   - column headers (round-trip-tested against the actual workbook .xlsx)
 *   - per-row validation (used by the dialog preview + the round-trip test)
 *   - the example row shown in the workbook + verified to round-trip
 *
 * The dialog, the bulk callable, and the round-trip test all consume the
 * same spec, so adding a column means updating exactly one file.
 *
 * Why a shared interface:
 *   - The round-trip test iterates a registry of specs and runs the same
 *     assertions against each (headers match, example row parses).
 *   - The dialog component is a thin generic wrapper that takes a spec +
 *     the list of already-existing entities (for duplicate detection) and
 *     renders the preview table.
 *   - The bulk callable's input is `{ rows: TParsed[] }` so the server
 *     stays out of the parsing business.
 */
import type { RowStatus } from "./importTypes";
import type { ImportIdentity } from "./identity";

export type { RowStatus } from "./importTypes";

/**
 * Per-cell metadata used by the workbook generator. Lives here so the
 * generator script and the admin parser stay in sync without import
 * gymnastics across the .mjs / .ts boundary.
 */
export interface ImportSpecColumn {
  /** Header name as it appears in row 1 of the sheet. */
  key: string;
  /** Human-readable note shown in row 2 of the sheet (italic, gray). */
  note: string;
}

/**
 * A single parsed row in either valid (`pending`) or invalid state.
 * `data` is always present so the preview can render whatever fields
 * parsed cleanly, even on otherwise-invalid rows.
 */
export interface ParsedRow<TData> {
  /** 1-based source row number (header = 1, first data = 2). */
  lineNumber: number;
  status: RowStatus;
  /** Human-readable message for invalid/failed rows; null on pending/success. */
  message: string | null;
  /** Best-effort parsed payload. Always present (may be partial on invalid rows). */
  data: TData;
}

/**
 * The contract a sheet must satisfy to be import-ready. `TRaw` is the
 * cell-map shape (everything is `string | undefined` after Excel parsing);
 * `TParsed` is the validated payload the bulk callable will accept.
 */
export interface ImportSpec<
  TRaw extends Record<string, string | undefined>,
  TParsed,
> {
  /** Sheet name in the organizer workbook (`event-planner-templates.xlsx`). */
  sheetName: string;
  /**
   * Short purpose blurb shown in the workbook generator (row 1 of the
   * sheet, merged across all columns). Mirrors the `purpose` field in the
   * generator's SHEETS array.
   */
  purpose: string;
  /** Column specs in display order. Drives both the workbook + the parser. */
  columns: readonly ImportSpecColumn[];
  /**
   * Example row shown in row 4 of the workbook. Round-trip tested: the
   * test reads this row from the actual .xlsx and asserts it parses to
   * `status: "pending"`. If the schema drifts away from the example, the
   * test fails.
   */
  example: TRaw;
  /**
   * Validate a single raw row. Implementations should:
   *   - normalize cell values (trim, lowercase emails, etc.)
   *   - return `status: "pending"` when the row is ready to import
   *   - return `status: "invalid"` with a non-null `message` otherwise
   *   - never throw — surface every failure via the message field
   */
  validate: (raw: TRaw, lineNumber: number) => ParsedRow<TParsed>;
  /**
   * Optional cross-row deduplication pass. Runs after `validate` for each
   * row and may flip a `pending` row to `invalid` (e.g. duplicate label
   * within the file). Defaults to identity (no extra dedup).
   */
  dedupe?: (rows: ParsedRow<TParsed>[]) => ParsedRow<TParsed>[];
  /**
   * Optional stable identity strategy for sheets that create documents.
   * Seed and audit use this to avoid row-order-based doc IDs while keeping
   * organizer-facing workbooks free of required opaque IDs.
   */
  identity?: ImportIdentity<TParsed>;
}

/** Helper: marks a row invalid with a message, preserving the parsed data. */
export function invalid<T>(
  parsed: Omit<ParsedRow<T>, "status" | "message">,
  message: string,
): ParsedRow<T> {
  return { ...parsed, status: "invalid", message };
}

/** Helper: marks a row pending (ready to import) with no message. */
export function pending<T>(
  parsed: Omit<ParsedRow<T>, "status" | "message">,
): ParsedRow<T> {
  return { ...parsed, status: "pending", message: null };
}

/**
 * Derive a `headers` array from the columns spec. Used by the round-trip
 * test to compare against the workbook's actual row-1 headers.
 */
export function headersOf<
  TRaw extends Record<string, string | undefined>,
  TParsed,
>(spec: ImportSpec<TRaw, TParsed>): string[] {
  return spec.columns.map((col) => col.key);
}

/**
 * Validate a list of raw rows against a spec, applying the spec's optional
 * dedup pass. Returns one ParsedRow per input.
 */
export function validateRows<
  TRaw extends Record<string, string | undefined>,
  TParsed,
>(
  spec: ImportSpec<TRaw, TParsed>,
  raws: TRaw[],
): ParsedRow<TParsed>[] {
  // Header row is line 1; first data row is line 2.
  const parsed = raws.map((raw, index) => spec.validate(raw, index + 2));
  return spec.dedupe ? spec.dedupe(parsed) : parsed;
}
